import React, { useState } from 'react';
import type { InteractiveButton } from '../types/message';

interface InteractiveMessageComposerProps {
  onSend: (bodyText: string, buttons: InteractiveButton[], footerText?: string) => void;
  onCancel: () => void;
  sending?: boolean;
}

const InteractiveMessageComposer: React.FC<InteractiveMessageComposerProps> = ({
  onSend,
  onCancel,
  sending = false,
}) => {
  const [bodyText, setBodyText] = useState('');
  const [buttons, setButtons] = useState<InteractiveButton[]>([
    { id: `btn_${Date.now()}_1`, title: '' },
  ]);
  const [footerText, setFooterText] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const addButton = () => {
    if (buttons.length >= 3) return;
    setButtons([...buttons, { id: `btn_${Date.now()}_${buttons.length + 1}`, title: '' }]);
  };

  const removeButton = (index: number) => {
    if (buttons.length <= 1) return;
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const updateButtonTitle = (index: number, title: string) => {
    const newButtons = [...buttons];
    newButtons[index] = { ...newButtons[index], title };
    setButtons(newButtons);
  };

  const validate = (): boolean => {
    const newErrors: string[] = [];

    if (!bodyText.trim()) {
      newErrors.push('Message body is required');
    }

    if (bodyText.length > 1024) {
      newErrors.push('Message body must be 1024 characters or less');
    }

    const filledButtons = buttons.filter((b) => b.title.trim());
    if (filledButtons.length === 0) {
      newErrors.push('At least one button is required');
    }

    buttons.forEach((btn, idx) => {
      if (btn.title.trim() && btn.title.length > 20) {
        newErrors.push(`Button ${idx + 1} title must be 20 characters or less`);
      }
    });

    // Check for duplicate titles
    const titles = filledButtons.map((b) => b.title.trim().toLowerCase());
    if (new Set(titles).size !== titles.length) {
      newErrors.push('Button titles must be unique');
    }

    if (footerText.length > 60) {
      newErrors.push('Footer must be 60 characters or less');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSend = () => {
    if (!validate()) return;

    const filledButtons = buttons
      .filter((b) => b.title.trim())
      .map((b) => ({ id: b.id, title: b.title.trim() }));

    onSend(bodyText.trim(), filledButtons, footerText.trim() || undefined);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />

      {/* Modal */}
      <div className="relative bg-warm-white rounded-xl shadow-sketch-lg w-full max-w-md mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-sketch flex items-center justify-between">
          <h2 className="text-lg font-semibold text-charcoal">Quick Reply Buttons</h2>
          <button
            onClick={onCancel}
            className="p-1 text-graphite/70 hover:text-graphite transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-brick/10 border border-brick/30 rounded-lg p-3">
              {errors.map((err, idx) => (
                <p key={idx} className="text-brick text-sm">{err}</p>
              ))}
            </div>
          )}

          {/* Body Text */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Message Body <span className="text-brick">*</span>
            </label>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder="Enter your message..."
              rows={3}
              className="w-full px-3 py-2 border border-sketch rounded-lg focus:ring-2 focus:ring-[#00a884] focus:border-transparent resize-none text-sm"
            />
            <p className="text-xs text-graphite/70 mt-1">{bodyText.length}/1024</p>
          </div>

          {/* Buttons */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-charcoal">
                Buttons <span className="text-brick">*</span>
                <span className="font-normal text-graphite/70 ml-1">(max 3)</span>
              </label>
              {buttons.length < 3 && (
                <button
                  onClick={addButton}
                  className="text-[#00a884] text-sm font-medium hover:text-[#008f72] transition-colors"
                >
                  + Add Button
                </button>
              )}
            </div>

            <div className="space-y-2">
              {buttons.map((btn, idx) => (
                <div key={btn.id} className="flex items-center gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={btn.title}
                      onChange={(e) => updateButtonTitle(idx, e.target.value)}
                      placeholder={`Button ${idx + 1} title`}
                      maxLength={20}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00a884] focus:border-transparent text-sm ${
                        btn.title.length > 20 ? 'border-brick/50' : 'border-sketch'
                      }`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-graphite/70">
                      {btn.title.length}/20
                    </span>
                  </div>
                  {buttons.length > 1 && (
                    <button
                      onClick={() => removeButton(idx)}
                      className="p-2 text-graphite/70 hover:text-brick transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer Text (Optional) */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-1">
              Footer <span className="text-graphite/70 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
              placeholder="e.g., Tap a button to respond"
              maxLength={60}
              className="w-full px-3 py-2 border border-sketch rounded-lg focus:ring-2 focus:ring-[#00a884] focus:border-transparent text-sm"
            />
            <p className="text-xs text-graphite/70 mt-1">{footerText.length}/60</p>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Preview</label>
            <div className="bg-[#e5ddd5] rounded-lg p-3">
              <div className="max-w-[240px] ml-auto">
                <div className="bg-[#dcf8c6] rounded-lg shadow-sketch overflow-hidden">
                  <div className="px-3 py-2">
                    <p className="text-[14px] text-charcoal whitespace-pre-wrap">
                      {bodyText || 'Your message will appear here...'}
                    </p>
                    {footerText && (
                      <p className="text-[11px] text-graphite mt-1">{footerText}</p>
                    )}
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5 justify-end">
                  {buttons.filter((b) => b.title.trim()).map((btn) => (
                    <div
                      key={btn.id}
                      className="px-3 py-1.5 bg-warm-white border border-sketch rounded-lg text-[12px] text-[#25d366] font-medium flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      {btn.title}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-sketch flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={sending}
            className="px-4 py-2 text-charcoal bg-cream rounded-lg hover:bg-charcoal/10 transition-colors text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !bodyText.trim() || buttons.every((b) => !b.title.trim())}
            className="px-4 py-2 bg-[#00a884] text-warm-white rounded-lg hover:bg-[#008f72] transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white">
                  <path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"/>
                </svg>
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMessageComposer;
